import { createBidirectionalMap } from './bidirectionalMap';
import type {
  AutomationActionType,
  AutomationConditionType,
  AutomationEntityType,
  AutomationTriggerEvent,
} from './types.generated';

export const automationActionTypeMap =
  createBidirectionalMap<AutomationActionType>()({
    EmailNotificationAutomationAction: 'E-mail Notification',
    SMSNotificationAutomationAction: 'SMS Notification',
    MoveProjectToPipelineStageAutomationAction:
      'Move Project to Pipeline stage',
    CreateProjectTaskAutomationAction: 'Create Project Task',
    TagEventAutomationAction: 'Tag Event',
    TagProjectAutomationAction: 'Tag Project',
    RemoveProjectTagsAutomationAction: 'Remove Project tags',
    RemoveEventTagsAutomationAction: 'Remove Event tags',
    CreateRepeatProjectAutomationAction: 'Create repeat Project',
  });

export const automationEntityTypeMap =
  createBidirectionalMap<AutomationEntityType>()({
    PROJECT: 'Deal',
    ESTIMATE: 'Estimate',
    QUOTE: 'Quote',
    INVOICE: 'Invoice',
    EVENT: 'Event',
    PROPOSAL: 'Proposal',
    DNO_APPLICATION: 'DNO Application',
    CHANGE_PROPOSAL: 'Change Proposal',
    DOCUMENT_INSTANCE: 'Document Instance',
  });

export const automationTriggerEventMap =
  createBidirectionalMap<AutomationTriggerEvent>()({
    CREATED: 'Created',
    SIGNATURE_ADDED: 'Signature added',
    ACCEPTED: 'Accepted',
    DECLINED: 'Declined',
    CANCELLED: 'Cancelled',
    SENT: 'Sent',
    EXPIRED: 'Expired',
    PAYMENT_RECEIVED: 'Payment Received',
    FULLY_PAID: 'Fully Paid',
    ASSIGNED: 'Assigned',
    TAGGED: 'Tagged',
    ADDED_TO_PIPELINE: 'Added to pipeline',
    PIPELINE_STAGE_CHANGED: 'Pipeline stage changed',
    TASK_STATUS_CHANGED: 'Task status changed',
    PIPELINE_STAGE_CHANGED_YESTERDAY: 'Pipeline stage changed yesterday',
    PIPELINE_STAGE_CHANGED_2_DAYS_AGO: 'Pipeline stage changed 2 days ago',
    PIPELINE_STAGE_CHANGED_3_DAYS_AGO: 'Pipeline stage changed 3 days ago',
    PIPELINE_STAGE_CHANGED_4_DAYS_AGO: 'Pipeline stage changed 4 days ago',
    PIPELINE_STAGE_CHANGED_5_DAYS_AGO: 'Pipeline stage changed 5 days ago',
    PIPELINE_STAGE_CHANGED_6_DAYS_AGO: 'Pipeline stage changed 6 days ago',
    PIPELINE_STAGE_CHANGED_7_DAYS_AGO: 'Pipeline stage changed 7 days ago',
    SCHEDULED_DAILY_RUN: 'Scheduled Daily Run',
    REALTIME_RUN: 'Realtime Run',
    DUE_IN_7_DAYS: 'Due in 7 days',
    DUE_IN_3_DAYS: 'Due in 3 days',
    DUE_IN_1_DAY: 'Due in 1 day',
    DUE_TODAY: 'Due today',
    DUE_YESTERDAY: 'Due yesterday',
    DUE_3_DAYS_AGO: 'Due 3 days ago',
    DUE_7_DAYS_AGO: 'Due 7 days ago',
    EXPIRES_IN_28_DAYS: 'Expires in 28 days',
    EXPIRES_IN_21_DAYS: 'Expires in 21 days',
    EXPIRES_IN_14_DAYS: 'Expires in 14 days',
    EXPIRES_IN_7_DAYS: 'Expires in 7 days',
    EXPIRES_IN_3_DAYS: 'Expires in 3 days',
    EXPIRES_IN_1_DAY: 'Expires in 1 day',
    EXPIRES_TODAY: 'Expires today',
    EXPIRED_YESTERDAY: 'Expired yesterday',
    EXPIRED_3_DAYS_AGO: 'Expired 3 days ago',
    EXPIRED_7_DAYS_AGO: 'Expired 7 days ago',
    STARTS_IN_3_MONTHS: 'Starts in 3 months',
    STARTS_IN_1_MONTH: 'Starts in 1 month',
    STARTS_IN_21_DAYS: 'Starts in 21 days',
    STARTS_IN_14_DAYS: 'Starts in 14 days',
    STARTS_IN_7_DAYS: 'Starts in 7 days',
    STARTS_IN_3_DAYS: 'Starts in 3 days',
    STARTS_IN_1_DAY: 'Starts in 1 day',
    STARTS_IN_1_HOUR: 'Starts in 1 hour',
    STARTS_NOW: 'Starts now',
    STARTS_TODAY: 'Starts today',
    FINISHES_NOW: 'Finishes now',
    FINISHES_TODAY: 'Finishes today',
    FINISHED_YESTERDAY: 'Finished yesterday',
    FINISHED_3_DAYS_AGO: 'Finished 3 days ago',
    FINISHED_7_DAYS_AGO: 'Finished 7 days ago',
    FINISHED_1_MONTH_AGO: 'Finished 1 month ago',
    FINISHED_11_MONTHS_AGO: 'Finished 11 months ago',
    FINISHED_1_YEAR_AGO: 'Finished 1 year ago',
    EVENT_TASK_STATUS_CHANGED: 'Event task status changed',
    CONNECTION_APPLICATION_STATUS_CHANGED:
      'Connection application status changed',
    ACCEPTANCE_REMOVED: 'Acceptance removed',
    VOIDED: 'Voided',
  });

export const automationConditionTypeMap =
  createBidirectionalMap<AutomationConditionType>()({
    CustomerNameCondition: 'customer.name',
    CustomerEmailCondition: 'primaryContact.emailAddress',
    LocationContactsEmailCondition:
      'event.location.contacts.emailaddress.array',
    LocationContactsPhoneCondition:
      'event.location.contacts.telephonenumber.array',
    ProjectSiteAddressContactsEmailCondition:
      'deal.siteaddresses.contacts.emailaddress.array',
    ProjectSiteAddressContactsPhoneCondition:
      'deal.siteaddresses.contacts.telephonenumber.array',
    NewPipelineStageNameCondition: 'trigger.newPipelineStage',
    OldPipelineStageNameCondition: 'trigger.oldPipelineStage',
    ConnectionApplicationStatusCondition:
      'connectionApplication.connectionApplicationStatus',
    CustomerPhoneCondition: 'primaryContact.telephoneNumber',
    UserEmailCondition: 'user.email',
    EventAssigneesEmailCondition: 'event.assignedusers.emailaddress.array',
    AssigneesPhoneCondition: 'event.assignedusers.telephonenumber.array',
    EventTaskStatusCondition: 'event.taskStatus',
    NewlyAddedTagCondition: 'trigger.X.tagText',
    ProjectPipelineCondition: 'deal.pipelineId',
    ProjectPipelineStageNameCondition: 'deal.pipelineStage',
    ProposalStatusCondition: 'proposal.readableStatus',
    InvoiceStatusCondition: 'invoice.readableStatus',
    InvoiceIsDepositCondition: 'invoice.isDepositInvoice',
    InvoiceIsGrantCondition: 'invoice.isGrantInvoice',
    TriggeredDayOfWeekCondition: 'trigger.triggeredAt.dayOfWeek',
    EventHasTagCondition: 'event.tags.X.tagText',
    EventDoesNotHaveTagCondition: 'event.not.tags.X.tagText',
    ProjectHasTagCondition: 'deal.tags.X.tagText',
    ProjectDoesNotHaveTagCondition: 'deal.not.tags.X.tagText',
  });
